import { FrontTable } from '@/lib';
import { ColumnDef } from '@tanstack/react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SmsSenderListActionsCell } from './SmsSenderListActionsCell';
import { SmsSender } from './types';

type SmsSenderTableProps = {
  senders: SmsSender[];
  deleteFlowId?: string;
  connectorInstanceId?: string;
  isEditReady?: boolean;
  isDeleteReady?: boolean;
  onDeleted: () => void;
};

export const SmsSenderTable: FC<SmsSenderTableProps> = ({ senders, deleteFlowId, connectorInstanceId, isEditReady, isDeleteReady, onDeleted }) => {
  const { t } = useTranslation('campaign');

  const columns = useMemo<ColumnDef<SmsSender>[]>(
    () => [
      {
        accessorKey: 'alias',
        header: t('smsWorkspace.senders.table.alias'),
        cell: ({ row }) => <span className='font-semibold text-slate-900'>{row.original.alias}</span>,
      },
      {
        accessorKey: 'type',
        header: t('smsWorkspace.senders.table.type'),
        cell: ({ row }) => (
          <span className='text-secondary-700 text-sm'>
            {t(`smsWorkspace.senders.type.${row.original.type === 'ALPHANUMERIC' ? 'alphanumeric' : 'virtualNumber'}`)}
          </span>
        ),
      },
      {
        accessorKey: 'registeredCompany',
        header: t('smsWorkspace.senders.table.status'),
        cell: ({ row }) => <span className='text-secondary-700 text-sm'>{row.original.registeredCompany ?? '—'}</span>,
      },
      {
        id: 'actions',
        header: t('smsWorkspace.senders.table.actions'),
        size: 120,
        maxSize: 120,
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <SmsSenderListActionsCell
            sender={row.original}
            deleteFlowId={deleteFlowId}
            connectorInstanceId={connectorInstanceId}
            isEditDisabled={!isEditReady}
            isDeleteDisabled={true}
            onDeleted={onDeleted}
          />
        ),
      },
    ],
    [connectorInstanceId, deleteFlowId, isDeleteReady, isEditReady, onDeleted, t]
  );

  return <FrontTable tableData={senders} columns={columns} notFound={t('smsWorkspace.senders.emptyTitle')} />;
};
